import type {
	FieldCategory,
	FieldOperations,
} from '@atlassian/jira-common-rest/src/internal/simplified/1.0/projects/id/issuetypes/id/types.tsx';
import type { FieldItemOperationsFragment as GraphqlFieldItemOperations } from '../fragments/__generated_apollo__/FieldItemOperationsFragment';
import type { FieldOperationsTransformer, Transformers } from './types.tsx';

/**
 * Transform field operation data from GraphQL into a SWIFT compatible {@link FieldOperations} object.
 */
export const transformFieldOperations: FieldOperationsTransformer = (
	operations: GraphqlFieldItemOperations,
	transformers: Transformers,
): FieldOperations => {
	const {
		editable,
		canModifyRequired,
		canModifyOptions,
		canModifyDefaultValue,
		canModifyPropertyConfiguration,
		removable,
		deletable,
		canAssociateInSettings,
		categoriesWhitelist,
	} = operations || {};

	const cleanCategoriesWhitelist: FieldCategory[] = categoriesWhitelist.reduce<
		Array<FieldCategory>
	>((categoryAccum: Array<FieldCategory>, categoryWhitelist: string) => {
		if (categoryWhitelist === undefined || categoryWhitelist === null) {
			return categoryAccum;
		}
		// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
		return [...categoryAccum, transformers.category(categoryWhitelist)];
	}, []);

	return {
		editable,
		canModifyRequired,
		canModifyOptions,
		canModifyDefaultValue,
		canModifyPropertyConfiguration,
		removable,
		deletable,
		canAssociateInSettings,
		categoriesWhitelist: cleanCategoriesWhitelist,
	};
};

/**
 * Perform GraphQL type refinements before delegating to the defined {@link FieldOperationsTransformer}.
 */
export const transformFieldOperationsAdapter = (
	operations: GraphqlFieldItemOperations | null | undefined,
	transformers: Transformers,
): FieldOperations => {
	if (!operations) {
		throw new Error('Failed to load initial data. No field operations');
	}

	return transformers.fieldOperations(operations, transformers);
};
